import * as THREE from 'three';
import { createAvatar } from './nyc-kit';

/** CPU pose groups keep named limbs; shared GPU instances draw the whole crowd. */
export class AvatarCrowd {
  readonly group = new THREE.Group();
  private material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.88 });
  private buckets = new Map<string, { mesh: THREE.InstancedMesh; used: number }>();
  constructor(readonly maxActors = 48) {
    const shapes = new Map<string, { geometry: THREE.BufferGeometry; count: number }>();
    createAvatar().traverse((part) => {
      if (!(part instanceof THREE.Mesh)) return;
      const shape = shapes.get(part.geometry.uuid) || { geometry: part.geometry, count: 0 };
      shape.count++;
      shapes.set(part.geometry.uuid, shape);
    });
    for (const [key, shape] of shapes) {
      const mesh = new THREE.InstancedMesh(shape.geometry, this.material, shape.count * maxActors);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.castShadow = mesh.receiveShadow = true;
      mesh.frustumCulled = false; // At most 46 small actors in the compact, fixed-camera map.
      mesh.count = 0;
      this.group.add(mesh);
      this.buckets.set(key, { mesh, used: 0 });
    }
  }
  update(actors: THREE.Group[]) {
    for (const bucket of this.buckets.values()) bucket.used = 0;
    for (const actor of actors.slice(0, this.maxActors)) {
      actor.updateMatrixWorld(true);
      actor.traverse((part) => {
        if (!(part instanceof THREE.Mesh) || Array.isArray(part.material)) return;
        const bucket = this.buckets.get(part.geometry.uuid);
        if (!bucket) return;
        const index = bucket.used++;
        bucket.mesh.setMatrixAt(index, part.matrixWorld);
        bucket.mesh.setColorAt(index, (part.material as THREE.MeshStandardMaterial).color);
      });
    }
    for (const { mesh, used } of this.buckets.values()) {
      mesh.count = used;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) {
        mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
        mesh.instanceColor.needsUpdate = true;
      }
    }
  }
  dispose() {
    for (const bucket of this.buckets.values()) bucket.mesh.dispose();
    this.material.dispose();
  }
}
